<app-header-latest></app-header-latest>
<!-- HERO -->
<div class="med-hero">
  <div class="container-wrapper">
    <div>
      <div class="section-eyebrow">🏥 CLINICAL WORKFORCE</div>
      <h1>HR Built for<br><span style="color:#7DD3FC;">Healthcare Heroes</span></h1>
      <p>Manage clinical staff rosters, credential expirations, HIPAA-compliant HR records, and shift coverage — purpose-built for hospitals, clinics, and care facilities.</p>
      <div style="display:flex;gap:16px;flex-wrap:wrap;">
        <a href="#" class="btn btn-primary" style="background:#0284c7;">Schedule Clinical Demo</a>
        <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">See Compliance Features</a>
      </div>
    </div>
    <div>
      <div class="alert-widget">
        <div class="alert-title">⚠️ Credential Alert Center</div>
        <div class="alert-item"><span>Dr. Ananya Mehta — BLS Cert.</span><span class="alert-badge">Expires in 14 days</span></div>
        <div class="alert-item"><span>Nurse John Reyes — DEA License</span><span class="alert-badge">Expires in 7 days</span></div>
        <div class="alert-item"><span>Tech. Sara Lin — PACS Access</span><span class="alert-badge">Renewal Pending</span></div>
        <div class="alert-item"><span>Dr. Khalid Hassan — CME Credits</span><span class="alert-badge">12 hrs remaining</span></div>
      </div>
    </div>
  </div>
</div>

<!-- COMPLIANCE CHECKER -->
<section class="compliance-checker-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">⚖️ Regulatory Compliance</span>
      <h2 class="sec-h2">HIPAA, JCAHO &amp;<br><span class="gradient-text">CMS — All Covered</span></h2>
      <p class="sec-sub">NeoHRMZ enforces healthcare-specific compliance rules so your facility stays audit-ready 365 days a year.</p>
    </div>
    <div class="checker-grid">
      <div class="checker-card">
        <div class="checker-card-icon">🔐</div>
        <div class="checker-card-title">HIPAA Access Controls</div>
        <div class="checker-card-desc">Role-based access ensures every employee only sees the PHI they're authorized for — with automatic de-provisioning on separation.</div>
      </div>
      <div class="checker-card">
        <div class="checker-card-icon">📋</div>
        <div class="checker-card-title">JCAHO Staffing Standards</div>
        <div class="checker-card-desc">Automatic nurse-to-patient ratio checks and credential verification embedded into every scheduling decision before shifts are published.</div>
      </div>
      <div class="checker-card">
        <div class="checker-card-icon">🏥</div>
        <div class="checker-card-title">CMS Conditions of Participation</div>
        <div class="checker-card-desc">Built-in workflows for competency assessments, background checks, and mandatory in-service training tracking for CMS compliance.</div>
      </div>
      <div class="checker-card">
        <div class="checker-card-icon">🧾</div>
        <div class="checker-card-title">Background Check Integration</div>
        <div class="checker-card-desc">Connect with OIG exclusion lists and state license verification boards — flagging issues before new hires are onboarded to patient care areas.</div>
      </div>
      <div class="checker-card">
        <div class="checker-card-icon">📊</div>
        <div class="checker-card-title">Audit Trail Generation</div>
        <div class="checker-card-desc">Immutable logs of every HR action — hire, termination, credential update, schedule change — ready for Joint Commission or DOH inspection.</div>
      </div>
      <div class="checker-card">
        <div class="checker-card-icon">🔔</div>
        <div class="checker-card-title">Mandatory Training Alerts</div>
        <div class="checker-card-desc">Automated reminders for annual compliance training, safety certifications, and competency assessments — no staff falls through the cracks.</div>
      </div>
    </div>
  </div>
</section>

<!-- CREDENTIAL TRACKING -->
<section class="credential-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">🪪 License Management</span>
      <h2 class="sec-h2">Track Every License<br><span class="gradient-text">Before It Lapses</span></h2>
      <p class="sec-sub">Proactive expiration alerts, automated renewal workflows, and a real-time dashboard keep your clinical team fully credentialed.</p>
    </div>
    <div class="credential-grid">
      <div class="credential-dashboard">
        <div class="credential-dashboard__header">Active Staff Credentials — ICU Ward</div>
        <div class="credential-dashboard__list">
          <div class="credential-dashboard__item">
            <span class="credential-dashboard__name">Dr. Priya Nair — MD License</span>
            <span class="credential-dashboard__status credential-dashboard__status--active">Active</span>
          </div>
          <div class="credential-dashboard__item">
            <span class="credential-dashboard__name">Nurse Tom Bradley — RN License</span>
            <span class="credential-dashboard__status credential-dashboard__status--expiring">Expiring Soon</span>
          </div>
          <div class="credential-dashboard__item">
            <span class="credential-dashboard__name">Dr. Sarah Obi — ACLS Cert.</span>
            <span class="credential-dashboard__status credential-dashboard__status--active">Active</span>
          </div>
          <div class="credential-dashboard__item">
            <span class="credential-dashboard__name">Tech. Daniel Cruz — X-Ray License</span>
            <span class="credential-dashboard__status credential-dashboard__status--expired">Expired</span>
          </div>
          <div class="credential-dashboard__item">
            <span class="credential-dashboard__name">Nurse Aisha Roy — BLS Cert.</span>
            <span class="credential-dashboard__status credential-dashboard__status--expiring">Expiring Soon</span>
          </div>
        </div>
      </div>
      <div class="credential-features">
        <div class="credential-feat">
          <div class="credential-feat__icon">🔔</div>
          <div>
            <div class="credential-feat__title">90-60-30 Day Alerts</div>
            <div class="credential-feat__desc">Automated reminders sent to the employee and their supervisor at 90, 60, and 30 days before expiration — with escalation to HR.</div>
          </div>
        </div>
        <div class="credential-feat">
          <div class="credential-feat__icon">🔄</div>
          <div>
            <div class="credential-feat__title">One-Click Renewal Workflow</div>
            <div class="credential-feat__desc">Staff complete renewal submissions directly in NeoHRMZ — documents uploaded, verified, and stored with zero email chains.</div>
          </div>
        </div>
        <div class="credential-feat">
          <div class="credential-feat__icon">🚫</div>
          <div>
            <div class="credential-feat__title">Scheduling Block Rules</div>
            <div class="credential-feat__desc">Staff with expired required credentials are automatically blocked from being scheduled in roles that require them — preventing liability.</div>
          </div>
        </div>
        <div class="credential-feat">
          <div class="credential-feat__icon">📁</div>
          <div>
            <div class="credential-feat__title">Centralized Document Vault</div>
            <div class="credential-feat__desc">All licenses, certificates, and diplomas stored securely in each employee's profile — accessible instantly during accreditation surveys.</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ROSTER MANAGEMENT -->
<section class="roster-section">
  <div class="container-wrapper">
    <div class="sec-head">
      <span class="section-eyebrow">📅 Clinical Rostering</span>
      <h2 class="sec-h2">24/7 Coverage,<br><span class="gradient-text">Zero Gaps</span></h2>
      <p class="sec-sub">Intelligent rostering tools designed for the complexity of clinical environments — balancing patient safety with staff wellbeing.</p>
    </div>
    <div class="roster-grid">
      <div class="roster-card">
        <div class="roster-card__icon">🏥</div>
        <div class="roster-card__title">Ward-Level Scheduling</div>
        <div class="roster-card__desc">Schedule staff by ward, specialty, or skill level with pre-built templates for ICU, ER, OT, and general wards — ready to publish in minutes.</div>
        <div class="roster-card__badge">Smart Templates</div>
      </div>
      <div class="roster-card">
        <div class="roster-card__icon">🔄</div>
        <div class="roster-card__title">On-Call Management</div>
        <div class="roster-card__desc">Manage on-call rotations, emergency cover requests, and last-minute substitutions with automatic escalation and notification workflows.</div>
        <div class="roster-card__badge">Auto-Escalation</div>
      </div>
      <div class="roster-card">
        <div class="roster-card__icon">⚖️</div>
        <div class="roster-card__title">Equitable Load Balancing</div>
        <div class="roster-card__desc">AI-driven workload distribution ensures no clinician is consistently overloaded — tracking hours, nights, weekends, and holiday duties fairly.</div>
        <div class="roster-card__badge">AI-Assisted</div>
      </div>
      <div class="roster-card">
        <div class="roster-card__icon">📱</div>
        <div class="roster-card__title">Mobile Shift Management</div>
        <div class="roster-card__desc">Staff view their upcoming shifts, request swaps, and clock in from anywhere via the NeoHRMZ mobile app — no kiosk required.</div>
        <div class="roster-card__badge">iOS &amp; Android</div>
      </div>
      <div class="roster-card">
        <div class="roster-card__icon">🩺</div>
        <div class="roster-card__title">Specialty Skill Matching</div>
        <div class="roster-card__desc">Automatically match shifts to only credentialed staff — no cardiologist covering neurology, no NICU nurse in an adult ICU without training validation.</div>
        <div class="roster-card__badge">Credential-Aware</div>
      </div>
      <div class="roster-card">
        <div class="roster-card__icon">📊</div>
        <div class="roster-card__title">Staffing Analytics</div>
        <div class="roster-card__desc">Real-time dashboards showing coverage ratios, overtime exposure, agency spend, and projected staffing gaps — weeks in advance.</div>
        <div class="roster-card__badge">Predictive</div>
      </div>
    </div>
  </div>
</section>

<!-- CTA -->
<section class="healthcare-cta">
  <div class="container-wrapper">
    <h2 class="healthcare-cta__h2">Better HR, Better<br>Patient Outcomes</h2>
    <p class="healthcare-cta__p">Join 450+ healthcare organizations running compliant, efficient workforce operations with NeoHRMZ. HIPAA-certified and implementation-ready.</p>
    <div class="healthcare-cta__actions">
      <a href="#" class="btn btn-white">Request Clinical Demo</a>
      <a href="#" class="btn btn-outline" style="color:#fff;border-color:rgba(255,255,255,0.3);">Download HIPAA Compliance Sheet</a>
    </div>
  </div>
</section>
<app-peopleflow-footer></app-peopleflow-footer>